/**
 * Preference centre token form (EMAIL-016).
 *
 * @package DemocraticTools
 */

/**
 * @param {object} [config]
 */
/**
 * Pending signups must keep the Subscribe CTA until email confirmation.
 */
export function clearPendingSubscriberHeader() {
  clearConfirmedSubscriber();
}

/**
 * Remember the browser that just subscribed so Manage Preferences can open
 * that subscriber's saved choices. No email is sent.
 *
 * @param {string} email
 * @param {string} preferencesUrl
 */
export function rememberSubscribedBrowser(email, preferencesUrl) {
  if (!String(email).includes('@') || !String(preferencesUrl).includes('token=')) {
    return;
  }

  writeSubscriberCookie(initialsFromEmail(email), preferencesUrl);
  applySubscriberEntryPoints();
}

export async function initPreferences(config) {
  const resolved = config ?? globalThis.dtPreferencesConfig ?? {};
  applyHomeStatusBanner(resolved);
  applyHeaderSubscriberMark();
  const form = document.querySelector(resolved.selectors?.form ?? '.dt-preferences-form');

  if (!isFormElement(form)) {
    return;
  }

  const token = readToken();
  const status = form.querySelector('.dt-form__status');
  const restUrl = appendToken(readAction(form), token);
  const tokenField = form.querySelector('#dt-preferences-token, [name="token"]');

  if (isFieldControl(tokenField)) {
    tokenField.value = token;
  }

  applyUrlStatusBanner(status, resolved);
  bindFrequencyRadios(form);
  bindCheckAll(form);

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    if (token === '') {
      setStatus(status, resolved.messages?.missing_token ?? 'Open this page from your secure email link.', 'error');
      return;
    }

    void savePreferences(form, restUrl, status, resolved);
  });

  const unsubButton = form.querySelector('[data-dt-preferences-unsubscribe]');

  if (isElement(unsubButton)) {
    unsubButton.addEventListener('click', () => {
      void unsubscribeAll(form, restUrl, status, resolved, token);
    });
  }

  form.addEventListener('click', (event) => {
    const target = event.target;

    if (!isElement(target)) {
      return;
    }

    const productButton = target.closest('[data-dt-product-unsubscribe]');

    if (!isElement(productButton)) {
      return;
    }

    event.preventDefault();
    const productCode = productButton.getAttribute('data-dt-product-unsubscribe') ?? '';

    if (productCode === '') {
      return;
    }

    void unsubscribePublicationProduct(form, restUrl, status, resolved, token, productCode);
  });

  if (token === '') {
    if (!hasUrlStatus()) {
      setStatus(status, resolved.messages?.missing_token ?? 'Open this page from your secure email link.', 'error');
    }
    return;
  }

  await loadPreferences(form, restUrl, status, resolved);
}

/**
 * Merge nested `subscriber` profile onto the top-level payload.
 *
 * @param {Record<string, unknown>} payload
 * @returns {Record<string, unknown>}
 */
export function normalizePreferencePayload(payload) {
  if (!payload || typeof payload !== 'object') {
    return {};
  }

  const nested = payload.subscriber;

  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return { ...payload, ...nested };
  }

  return payload;
}

/**
 * @param {HTMLFormElement} form
 * @param {string} restUrl
 * @param {HTMLElement|null} status
 * @param {object} config
 */
export async function loadPreferences(form, restUrl, status, config) {
  try {
    const response = await fetch(restUrl, { headers: { Accept: 'application/json' } });

    if (!response.ok) {
      // Keep post-confirm / post-unsubscribe success banners; do not flash them away.
      if (!urlStatusIsSuccess() && !isSaving(form)) {
        const payload = await parseJsonSafe(response);
        setStatus(
          status,
          readableMessage(payload.message)
            || config.messages?.invalid_token
            || 'This preference link is invalid or has expired.',
          'error',
        );
      }
      return;
    }

    const payload = await response.json();

    if (isSaving(form)) {
      return;
    }

    populateForm(form, payload);
    syncConfirmedSubscriberMark(payload);

    if (!urlStatusIsSuccess()) {
      setStatus(
        status,
        config.messages?.showing_saved ?? 'Showing your currently saved preferences.',
        'success',
      );
    }
  } catch {
    if (!urlStatusIsSuccess() && !isSaving(form)) {
      setStatus(status, config.messages?.network_error ?? 'We could not load your preferences.', 'error');
    }
  } finally {
    applyHeaderSubscriberMark();
  }
}

/**
 * @param {HTMLFormElement} form
 * @param {string} restUrl
 * @param {HTMLElement|null} status
 * @param {object} config
 */
export async function savePreferences(form, restUrl, status, config) {
  beginSaveUi(form, status, config);

  const frequency = readFrequency(form);
  const body = {
    token: readToken(),
    frequency,
    frequency_pref: frequency,
    locale: readValue(form, 'locale'),
    role_preferences: readIdValues(form, 'role_preferences'),
    market_preferences: readIdValues(form, 'market_preferences'),
    topic_preferences: readIdValues(form, 'topic_preferences'),
    topic_ids: readIdValues(form, 'topic_preferences'),
    primary_role: readPrimaryRole(form),
    market_codes: readMarketCodes(form),
  };
  const commercialConsent = readCommercialConsent(form);

  if (form.querySelector('input[name="commercial_consent"]')) {
    body.commercial_consent = commercialConsent;
  }

  if (form.querySelector('[data-dt-publications-host]')) {
    body.product_subscriptions = readProductSubscriptions(form);
  }

  try {
    const response = await fetch(restUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    const payload = await response.json();

    if (!response.ok) {
      setStatus(
        status,
        readableMessage(payload.message)
          || config.messages?.generic_error
          || 'Could not save preferences.',
        'error',
      );
      return;
    }

    populateForm(form, payload);
    syncConfirmedSubscriberMark(payload);
    setStatus(status, readableMessage(payload.message) || config.messages?.saved || 'Preferences saved.', 'success');
  } catch {
    setStatus(status, config.messages?.network_error ?? 'We could not save your preferences.', 'error');
  } finally {
    endSaveUi(form);
  }
}

/**
 * @param {HTMLFormElement} form
 * @param {string} restUrl
 * @param {HTMLElement|null} status
 * @param {object} config
 * @param {string} token
 */
/**
 * @param {HTMLFormElement} form
 * @param {string} restUrl
 * @param {HTMLElement|null} status
 * @param {object} config
 * @param {string} token
 * @param {string} productCode
 */
export async function unsubscribePublicationProduct(form, restUrl, status, config, token, productCode) {
  if (token === '') {
    setStatus(status, config.messages?.missing_token ?? 'Open this page from your secure email link.', 'error');
    return;
  }

  const confirmed = globalThis.confirm(
    config.messages?.product_unsubscribe_confirm
      ?? 'Unsubscribe from this publication only? Your other subscriptions will stay active.',
  );

  if (!confirmed) {
    return;
  }

  setStatus(status, config.messages?.product_unsubscribe_saving ?? 'Updating subscription…', 'polite');

  try {
    const response = await fetch(restUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, unsubscribe_product: productCode }),
    });
    const payload = await response.json();

    if (!response.ok) {
      setStatus(
        status,
        readableMessage(payload.message)
          || config.messages?.generic_error
          || 'Could not update subscription.',
        'error',
      );
      return;
    }

    populateForm(form, payload);
    syncConfirmedSubscriberMark(payload);
    setStatus(
      status,
      readableMessage(payload.message)
        || config.messages?.product_unsubscribed
        || 'You have been unsubscribed from that publication.',
      'success',
    );
  } catch {
    setStatus(status, config.messages?.network_error ?? 'We could not update your subscription.', 'error');
  }
}

export async function unsubscribeAll(form, restUrl, status, config, token) {
  if (token === '') {
    setStatus(status, config.messages?.missing_token ?? 'Open this page from your secure email link.', 'error');
    return;
  }

  const confirmed = globalThis.confirm(
    config.messages?.unsubscribe_confirm
      ?? 'Are you sure you want to unsubscribe from all Local Democracy Works communications?',
  );

  if (!confirmed) {
    return;
  }

  beginUnsubscribeUi(form, status, config);

  try {
    const response = await fetch(restUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, unsubscribe: true }),
    });
    const payload = await response.json();

    if (!response.ok) {
      setStatus(
        status,
        readableMessage(payload.message)
          || config.messages?.generic_error
          || 'Could not unsubscribe.',
        'error',
      );
      endUnsubscribeUi(form);
      return;
    }

    form.setAttribute('data-unsubscribed', '1');
    form.removeAttribute('data-dt-preferences-busy');
    redirectToHomeAfterUnsubscribe();
  } catch {
    setStatus(status, config.messages?.network_error ?? 'We could not complete unsubscribe.', 'error');
    endUnsubscribeUi(form);
  }
}

/**
 * @param {HTMLFormElement} form
 * @param {Record<string, unknown>} payload
 */
export function populateForm(form, payload) {
  const data = normalizePreferencePayload(payload);

  setEmailDisplay(form, data.email);
  setFrequency(form, data.frequency ?? data.frequency_pref);
  setValue(form, 'locale', data.locale);
  setRolePreferences(form, data.role_preferences, data.primary_role);
  setMarketPreferences(form, data.market_preferences, data.market_codes);
  setMultiOrText(
    form,
    'topic_preferences',
    Array.isArray(data.topic_ids) && data.topic_ids.length > 0
      ? data.topic_ids
      : data.topic_preferences,
  );
  setCommercialConsent(form, data.commercial_consent);
  renderPublicationProducts(form, data);
  syncPreferenceCheckAll(form);
}

/**
 * @param {HTMLFormElement} form
 * @param {Record<string, unknown>} data
 */
export function renderPublicationProducts(form, data) {
  const host = form.querySelector('[data-dt-publications-host]');

  if (!isElement(host)) {
    return;
  }

  const normalized = normalizePreferencePayload(data);
  /** @type {Array<Record<string, unknown>>} */
  const available = Array.isArray(normalized.available_products) ? normalized.available_products : [];
  /** @type {Array<Record<string, unknown>>} */
  const current = Array.isArray(normalized.publication_products) ? normalized.publication_products : [];
  /** @type {Map<string, Record<string, unknown>>} */
  const byCode = new Map(
    current
      .filter((row) => row && typeof row === 'object')
      .map((row) => [String(row.product_code ?? ''), row]),
  );

  host.replaceChildren();

  if (available.length === 0) {
    host.setAttribute('aria-busy', 'false');
    host.hidden = true;
    return;
  }

  host.hidden = false;
  const frequencyOptions = readFrequencyOptions(form);

  for (const product of available) {
    if (!product || typeof product !== 'object') {
      continue;
    }

    const code = String(product.code ?? '').trim();

    if (code === '') {
      continue;
    }

    const row = byCode.get(code) ?? {};
    const statusKey = String(row.status ?? row.status_key ?? '');
    let subscribed = row.subscribed === true;

    if (!subscribed && statusKey !== '') {
      subscribed = statusKey === 'active' || statusKey === 'pending' || statusKey === 'paused';
    }

    if (!byCode.has(code) && code === 'main_edition') {
      subscribed = true;
    }

    const productName = String(product.name ?? code);
    const rowFrequency = String(row.frequency ?? product.default_frequency ?? readFrequency(form));

    const wrapper = document.createElement('div');
    wrapper.className = 'dt-preferences-form__product';
    wrapper.setAttribute('data-dt-product-row', '1');
    wrapper.setAttribute('data-product-code', code);

    const subscribeLabel = document.createElement('label');
    subscribeLabel.className = 'dt-choice dt-preferences-form__product-subscribe';
    const subscribeInput = document.createElement('input');
    subscribeInput.type = 'checkbox';
    subscribeInput.setAttribute('data-dt-product-subscribe', '1');
    subscribeInput.value = code;
    subscribeInput.checked = subscribed;
    subscribeLabel.append(subscribeInput, document.createTextNode(` ${productName}`));

    const freqLabel = document.createElement('label');
    freqLabel.className = 'dt-preferences-form__product-frequency-label';
    freqLabel.textContent = 'Frequency';

    const freqSelect = document.createElement('select');
    freqSelect.setAttribute('data-dt-product-frequency', '1');
    freqSelect.setAttribute('aria-label', `Frequency for ${productName}`);

    let frequencyMatched = false;

    for (const option of frequencyOptions) {
      const el = document.createElement('option');
      el.value = option.value;
      el.textContent = option.label;

      if (option.value === rowFrequency) {
        el.selected = true;
        frequencyMatched = true;
      }

      freqSelect.append(el);
    }

    if (!frequencyMatched && freqSelect.options.length > 0) {
      freqSelect.options[0].selected = true;
    }

    freqSelect.disabled = !subscribed;

    subscribeInput.addEventListener('change', () => {
      freqSelect.disabled = !subscribeInput.checked;
    });

    const unsub = document.createElement('button');
    unsub.type = 'button';
    unsub.className = 'dt-button dt-button--ghost dt-preferences-form__product-unsubscribe';
    unsub.setAttribute('data-dt-product-unsubscribe', code);
    unsub.textContent = 'Unsubscribe from this publication';

    if (String(product.description ?? '').trim() !== '') {
      const desc = document.createElement('p');
      desc.className = 'dt-form__help';
      desc.textContent = String(product.description);
      wrapper.append(desc);
    }

    wrapper.append(subscribeLabel, freqLabel, freqSelect, unsub);
    host.append(wrapper);
  }

  host.setAttribute('aria-busy', 'false');
}

/**
 * @param {HTMLFormElement} form
 * @returns {Array<{ code: string, frequency: string, subscribed: boolean }>}
 */
export function readProductSubscriptions(form) {
  /** @type {Array<{ code: string, frequency: string, subscribed: boolean }>} */
  const items = [];

  form.querySelectorAll('[data-dt-product-row]').forEach((row) => {
    if (!isElement(row)) {
      return;
    }

    const code = row.getAttribute('data-product-code') ?? '';
    const subscribe = row.querySelector('[data-dt-product-subscribe]');
    const frequency = row.querySelector('[data-dt-product-frequency]');
    const subscribed = isFieldControl(subscribe) && subscribe.type === 'checkbox' ? subscribe.checked : false;
    const freqValue = isFieldControl(frequency) && frequency.tagName === 'SELECT'
      ? frequency.value
      : readFrequency(form);

    if (code === '') {
      return;
    }

    items.push({
      code,
      frequency: freqValue,
      subscribed,
    });
  });

  return items;
}

/**
 * @param {HTMLFormElement} form
 * @returns {Array<{ value: string, label: string }>}
 */
function readFrequencyOptions(form) {
  /** @type {Array<{ value: string, label: string }>} */
  const options = [];

  form.querySelectorAll('input[name="frequency_pref"]').forEach((input) => {
    if (!isFieldControl(input) || input.type !== 'radio') {
      return;
    }

    const value = input.value.trim();

    if (value === '') {
      return;
    }

    const label = input.closest('label')?.textContent?.trim() ?? value;
    options.push({ value, label });
  });

  if (options.length === 0) {
    return [
      { value: 'weekly', label: 'Weekly' },
      { value: 'biweekly', label: 'Biweekly' },
      { value: 'monthly', label: 'Monthly' },
      { value: 'paused', label: 'Paused' },
    ];
  }

  return options;
}

/**
 * @param {HTMLFormElement} form
 * @param {unknown} email
 */
function setEmailDisplay(form, email) {
  const value = typeof email === 'string' ? email.trim() : String(email ?? '').trim();
  const display = form.querySelector('#dt-preferences-email-display');
  const field = form.querySelector('#dt-preferences-email, input[name="email"]');

  if (isElement(display)) {
    display.textContent = value !== '' ? value : '—';
  }

  if (isFieldControl(field)) {
    field.setAttribute('value', value);
    field.value = value;
  }
}

/**
 * @param {unknown} value
 */
export function joinIds(value) {
  return Array.isArray(value) ? value.map(String).join(', ') : '';
}

/**
 * @param {string} raw
 * @returns {number[]}
 */
export function parseIdList(raw) {
  return raw.split(',')
    .map((part) => Number.parseInt(part.trim(), 10))
    .filter((id) => Number.isFinite(id) && id > 0);
}

/**
 * @param {string} baseUrl
 * @param {string} token
 */
export function appendToken(baseUrl, token) {
  const url = new URL(baseUrl, globalThis.location?.origin ?? 'https://example.test');
  url.searchParams.set('token', token);
  return url.toString();
}

/**
 * @param {HTMLFormElement} form
 */
function readAction(form) {
  const action = form.getAttribute('action');
  return typeof action === 'string' && action !== '' ? action : '';
}

function readToken() {
  return new URLSearchParams(globalThis.location?.search ?? '').get('token') ?? '';
}

function hasUrlStatus() {
  const status = new URLSearchParams(globalThis.location?.search ?? '').get('status') ?? '';
  return status !== '';
}

function urlStatusIsSuccess() {
  const code = new URLSearchParams(globalThis.location?.search ?? '').get('status') ?? '';
  return code === 'confirmed' || code === 'unsubscribed';
}

/**
 * @param {HTMLElement|null} status
 * @param {object} config
 */
/**
 * Show an unsubscribed confirmation on the home subscribe form after redirect.
 *
 * @param {object} config
 */
export function applyHomeStatusBanner(config) {
  const code = new URLSearchParams(globalThis.location?.search ?? '').get('status') ?? '';

  if (code !== 'unsubscribed') {
    return;
  }

  clearConfirmedSubscriber();

  const subscribeForm = document.querySelector('.dt-subscribe-form');

  if (!isFormElement(subscribeForm)) {
    return;
  }

  const status = subscribeForm.querySelector('.dt-form__status');
  applyUrlStatusBanner(status, config);
}

function redirectToHomeAfterUnsubscribe() {
  clearConfirmedSubscriber();

  let target = '/?status=unsubscribed';
  const origin = globalThis.location?.origin;

  if (typeof origin === 'string' && origin !== '') {
    const home = new URL('/', origin);
    home.searchParams.set('status', 'unsubscribed');
    target = home.toString();
  }

  if (typeof globalThis.location?.replace === 'function') {
    globalThis.location.replace(target);
    return;
  }

  if (typeof globalThis.location?.assign === 'function') {
    globalThis.location.assign(target);
  }
}

export function applyUrlStatusBanner(status, config) {
  const code = new URLSearchParams(globalThis.location?.search ?? '').get('status') ?? '';

  if (code === '') {
    return;
  }

  const messages = {
    confirmed: config.messages?.confirmed ?? 'Subscription confirmed. Your preference details are active below.',
    unsubscribed: config.messages?.unsubscribed ?? 'You have been unsubscribed from all Local Democracy Works communications.',
    invalid_token: config.messages?.invalid_token ?? 'The confirmation or unsubscribe link was invalid or has expired.',
    invalid: config.messages?.invalid_token ?? 'The confirmation or unsubscribe link was invalid or has expired.',
    not_found: config.messages?.not_found ?? 'We could not find the subscriber record for this link.',
  };

  const type = code === 'confirmed' || code === 'unsubscribed' ? 'success' : 'error';
  setStatus(status, messages[code] ?? messages.invalid_token, type);
}

function preferenceCheckboxes(form, name) {
  return Array.from(form.querySelectorAll(`input[name="${name}"]`))
    .filter((input) => isElement(input) && input.type === 'checkbox');
}

/**
 * @param {HTMLFormElement} form
 * @param {string} name
 * @returns {number[]}
 */
function readIdValues(form, name) {
  const checkboxes = preferenceCheckboxes(form, name);

  if (checkboxes.length > 0) {
    return checkboxes
      .filter((input) => input.checked === true)
      .map((input) => Number.parseInt(input.value, 10))
      .filter((id) => Number.isFinite(id) && id > 0);
  }

  const field = form.querySelector(`[name="${name}"]`);

  if (!isElement(field)) {
    return [];
  }

  if (field.tagName === 'SELECT' && isMultiSelect(field)) {
    return selectedSelectOptions(field)
      .map((option) => Number.parseInt(option.value, 10))
      .filter((id) => Number.isFinite(id) && id > 0);
  }

  if (isFieldControl(field)) {
    return parseIdList(field.value);
  }

  return [];
}

/**
 * @param {Element} field
 */
function isMultiSelect(field) {
  return field.multiple === true
    || (typeof field.hasAttribute === 'function' && field.hasAttribute('multiple'));
}

/**
 * @param {Element} field
 * @returns {HTMLOptionElement[]}
 */
function selectedSelectOptions(field) {
  if (field.selectedOptions && field.selectedOptions.length > 0) {
    return Array.from(field.selectedOptions);
  }

  return Array.from(field.options ?? []).filter((option) => option.selected);
}

/**
 * @param {HTMLFormElement} form
 * @param {string} name
 * @param {unknown} value
 */
function setMultiOrText(form, name, value) {
  const ids = Array.isArray(value) ? value.map(String) : [];
  const checkboxes = preferenceCheckboxes(form, name);

  if (checkboxes.length > 0) {
    checkboxes.forEach((input) => {
      input.checked = ids.includes(input.value);
    });
    return;
  }

  const field = form.querySelector(`[name="${name}"]`);

  if (!isElement(field)) {
    return;
  }

  if (field.tagName === 'SELECT' && 'options' in field) {
    Array.from(field.options).forEach((option) => {
      option.selected = ids.includes(option.value);
    });
    return;
  }

  setValue(form, name, joinIds(value));
}

/**
 * @param {HTMLFormElement} form
 * @param {unknown} prefs
 * @param {unknown} primaryRole
 */
function setRolePreferences(form, prefs, primaryRole) {
  const ids = Array.isArray(prefs) ? prefs.map(String) : [];
  const primary = primaryRole !== undefined && primaryRole !== null && String(primaryRole) !== '0'
    ? String(primaryRole)
    : '';

  if (primary !== '' && !ids.includes(primary)) {
    ids.push(primary);
  }

  setMultiOrText(form, 'role_preferences', ids);

  const radios = Array.from(form.querySelectorAll('input[name="primary_role"]'))
    .filter((input) => isElement(input) && input.type === 'radio');

  if (radios.length > 0 && primary !== '') {
    radios.forEach((input) => {
      input.checked = input.value === primary;
    });
  }
}

/**
 * @param {HTMLFormElement} form
 * @param {unknown} prefs
 * @param {unknown} codes
 */
function setMarketPreferences(form, prefs, codes) {
  const ids = Array.isArray(prefs) ? prefs.map(String) : [];
  const codeList = Array.isArray(codes)
    ? codes.map((code) => String(code).toLowerCase()).filter((code) => code !== '')
    : [];
  const checkboxes = preferenceCheckboxes(form, 'market_preferences');

  if (checkboxes.length > 0) {
    checkboxes.forEach((input) => {
      const code = String(input.getAttribute('data-market-code') ?? '').toLowerCase();
      input.checked = ids.includes(input.value) || (code !== '' && codeList.includes(code));
    });
    return;
  }

  setMultiOrText(form, 'market_preferences', prefs);
}

/**
 * @param {HTMLFormElement} form
 * @param {unknown} value
 */
function setFrequency(form, value) {
  const next = typeof value === 'string' ? value : String(value ?? '');
  const radios = Array.from(form.querySelectorAll('input[name="frequency_pref"], input[name="frequency"]'))
    .filter((input) => isElement(input) && input.type === 'radio');

  if (radios.length > 0) {
    radios.forEach((input) => {
      input.checked = next !== '' && input.value === next;
    });
  }

  Array.from(form.querySelectorAll('[name="frequency"], [name="frequency_pref"]')).forEach((field) => {
    if (!isFieldControl(field) || field.type === 'radio') {
      return;
    }

    try {
      field.value = next;
    } catch {
      field.setAttribute('value', next);
    }
  });
}

/**
 * @param {HTMLFormElement} form
 */
function bindFrequencyRadios(form) {
  form.querySelectorAll('input[type="radio"][name="frequency_pref"], input[type="radio"][name="frequency"]').forEach((input) => {
    input.addEventListener('change', () => {
      if (input.checked) {
        setFrequency(form, input.value);
      }
    });
  });
}

const PREFERENCE_GROUPS = ['role_preferences', 'market_preferences', 'topic_preferences'];

/**
 * @param {HTMLFormElement} form
 */
function bindCheckAll(form) {
  form.querySelectorAll('[data-dt-check-all]').forEach((control) => {
    if (!isElement(control) || control.tagName !== 'INPUT') {
      return;
    }

    control.addEventListener('change', () => {
      const name = control.getAttribute('data-dt-check-all') || '';
      preferenceCheckboxes(form, name).forEach((input) => {
        input.checked = control.checked === true;
      });
      syncCheckAll(form, name);
    });
  });

  PREFERENCE_GROUPS.forEach((name) => {
    preferenceCheckboxes(form, name).forEach((input) => {
      input.addEventListener('change', () => {
        syncCheckAll(form, name);
      });
    });

    const field = form.querySelector(`select[name="${name}"]`);

    if (isElement(field) && isMultiSelect(field)) {
      field.addEventListener('change', () => {
        applySelectCheckAll(field);
      });
    }
  });
}

/**
 * Keep Check all in step with the boxes or options underneath it.
 *
 * @param {HTMLFormElement} form
 */
function syncPreferenceCheckAll(form) {
  PREFERENCE_GROUPS.forEach((name) => {
    syncCheckAll(form, name);
    const field = form.querySelector(`select[name="${name}"]`);

    if (isElement(field) && isMultiSelect(field)) {
      syncSelectCheckAll(field);
    }
  });
}

/**
 * @param {HTMLFormElement} form
 * @param {string} name
 */
function syncCheckAll(form, name) {
  const control = form.querySelector(`input[data-dt-check-all="${name}"]`);

  if (!isFieldControl(control) || control.type !== 'checkbox') {
    return;
  }

  const boxes = preferenceCheckboxes(form, name);

  if (boxes.length === 0) {
    return;
  }

  const checkedCount = boxes.filter((input) => input.checked === true).length;
  control.checked = checkedCount === boxes.length;
  control.indeterminate = checkedCount > 0 && checkedCount < boxes.length;
}

/**
 * @param {Element} select
 */
function applySelectCheckAll(select) {
  const allOption = checkAllOption(select);

  if (!allOption) {
    return;
  }

  const options = realSelectOptions(select);
  const wasAll = select.getAttribute('data-dt-all-selected') === '1';

  if (allOption.selected && !wasAll) {
    options.forEach((option) => {
      option.selected = true;
    });
  } else if (!allOption.selected && wasAll) {
    options.forEach((option) => {
      option.selected = false;
    });
  }

  syncSelectCheckAll(select);
}

/**
 * @param {Element} select
 */
function syncSelectCheckAll(select) {
  const allOption = checkAllOption(select);

  if (!allOption) {
    return;
  }

  const options = realSelectOptions(select);
  const allSelected = options.length > 0 && options.every((option) => option.selected);
  allOption.selected = allSelected;

  if (allSelected) {
    select.setAttribute('data-dt-all-selected', '1');
    return;
  }

  select.removeAttribute('data-dt-all-selected');
}

/**
 * @param {Element} select
 * @returns {HTMLOptionElement|undefined}
 */
function listedOptions(select) {
  return Array.from(select.options ?? []);
}

function checkAllOption(select) {
  return listedOptions(select).find((option) => option.hasAttribute('data-dt-check-all'));
}

/**
 * @param {Element} select
 * @returns {HTMLOptionElement[]}
 */
function realSelectOptions(select) {
  return listedOptions(select).filter(
    (option) => !option.hasAttribute('data-dt-check-all') && option.value !== '',
  );
}

/**
 * @param {HTMLFormElement} form
 */
function readFrequency(form) {
  const checked = Array.from(
    form.querySelectorAll('input[type="radio"][name="frequency_pref"], input[type="radio"][name="frequency"]'),
  ).find((input) => isElement(input) && input.checked === true);

  if (isFieldControl(checked)) {
    return checked.value;
  }

  return readValue(form, 'frequency') || readValue(form, 'frequency_pref');
}

/**
 * @param {HTMLFormElement} form
 */
function readPrimaryRole(form) {
  const checked = Array.from(form.querySelectorAll('input[type="radio"][name="primary_role"]'))
    .find((input) => isElement(input) && input.checked === true);

  if (isFieldControl(checked)) {
    return Number.parseInt(checked.value, 10) || 0;
  }

  const roles = readIdValues(form, 'role_preferences');
  return roles[0] ?? 0;
}

/**
 * @param {HTMLFormElement} form
 * @returns {string[]}
 */
function readMarketCodes(form) {
  return preferenceCheckboxes(form, 'market_preferences')
    .filter((input) => input.checked === true)
    .map((input) => String(input.getAttribute('data-market-code') ?? '').toLowerCase())
    .filter((code) => code !== '');
}

/**
 * @param {HTMLFormElement} form
 */
function readCommercialConsent(form) {
  const field = form.querySelector('input[name="commercial_consent"]');

  if (!isElement(field)) {
    return false;
  }

  if (field.type === 'checkbox') {
    return field.checked === true;
  }

  return field.value === '1' || field.value === 'true';
}

/**
 * @param {HTMLFormElement} form
 * @param {unknown} value
 */
function setCommercialConsent(form, value) {
  const field = form.querySelector('input[name="commercial_consent"]');

  if (!isElement(field)) {
    return;
  }

  const enabled = value === true || value === 1 || value === '1' || value === 'true';

  if (field.type === 'checkbox') {
    field.checked = enabled;
    return;
  }

  field.value = enabled ? '1' : '0';
}

/**
 * @param {HTMLFormElement} form
 * @param {string} name
 */
function readValue(form, name) {
  const field = form.querySelector(`[name="${name}"]`);
  if (!isFieldControl(field)) {
    return '';
  }

  return field.value;
}

/**
 * @param {HTMLFormElement} form
 * @param {string} name
 * @param {unknown} value
 */
function setValue(form, name, value) {
  const field = form.querySelector(`[name="${name}"]`);

  if (!isFieldControl(field)) {
    return;
  }

  const next = typeof value === 'string' ? value : String(value ?? '');

  try {
    field.value = next;
  } catch {
    field.setAttribute('value', next);
  }
}

/**
 * @param {HTMLElement|null} status
 * @param {string} message
 * @param {'success' | 'error'} type
 */
function isSaving(form) {
  return form.getAttribute('data-dt-preferences-busy') === '1';
}

/**
 * Show Saving… on the status banner and the Save button as soon as save starts.
 *
 * @param {HTMLFormElement} form
 * @param {HTMLElement|null} status
 * @param {object} config
 */
function beginSaveUi(form, status, config) {
  const message = config.messages?.submitting ?? 'Saving…';
  form.setAttribute('data-dt-preferences-busy', '1');
  setStatus(status, message, '');

  const button = submitButton(form);

  if (!button) {
    return;
  }

  if (!button.dataset.dtOriginalLabel) {
    button.dataset.dtOriginalLabel = button.textContent;
  }

  button.textContent = message;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
}

/**
 * @param {HTMLFormElement} form
 */
function endSaveUi(form) {
  form.removeAttribute('data-dt-preferences-busy');
  const button = submitButton(form);

  if (!button) {
    return;
  }

  button.disabled = false;
  button.removeAttribute('aria-busy');
  button.textContent = button.dataset.dtOriginalLabel;
}

/**
 * Show Unsubscribing… on the status banner and disable actions while the request runs.
 *
 * @param {HTMLFormElement} form
 * @param {HTMLElement|null} status
 * @param {object} config
 */
function beginUnsubscribeUi(form, status, config) {
  const message = config.messages?.unsubscribing ?? 'Unsubscribing…';
  form.setAttribute('data-dt-preferences-busy', '1');
  setStatus(status, message, '');

  const button = unsubscribeButton(form);

  if (button) {
    if (!button.dataset.dtOriginalLabel) {
      button.dataset.dtOriginalLabel = button.textContent;
    }

    button.textContent = message;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
  }

  const saveButton = submitButton(form);

  if (saveButton) {
    saveButton.disabled = true;
  }
}

/**
 * @param {HTMLFormElement} form
 */
function endUnsubscribeUi(form) {
  form.removeAttribute('data-dt-preferences-busy');

  const button = unsubscribeButton(form);

  if (button) {
    button.disabled = false;
    button.removeAttribute('aria-busy');

    if (button.dataset.dtOriginalLabel) {
      button.textContent = button.dataset.dtOriginalLabel;
    }
  }

  const saveButton = submitButton(form);

  if (saveButton) {
    saveButton.disabled = false;
  }
}

/**
 * @param {HTMLFormElement} form
 * @returns {HTMLButtonElement|null}
 */
function submitButton(form) {
  const button = form.querySelector('button[type="submit"]');
  return isElement(button) ? button : null;
}

/**
 * @param {HTMLFormElement} form
 * @returns {HTMLButtonElement|null}
 */
function unsubscribeButton(form) {
  const button = form.querySelector('[data-dt-preferences-unsubscribe]');
  return isElement(button) ? button : null;
}

const SUBSCRIBER_INITIALS_COOKIE = 'dt_subscriber_initials';

/**
 * Two letters from the mailbox name: yvonne.onuorah → YO.
 *
 * @param {string} email
 * @returns {string}
 */
export function initialsFromEmail(email) {
  const local = String(email).split('@')[0] || '';
  const parts = local.split(/[._+-]+/).filter((part) => part !== '');

  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }

  const letters = local.replace(/[^a-zA-Z]/g, '');

  return (letters.slice(0, 2) || 'S').toUpperCase();
}

/**
 * @param {Record<string, unknown>|null|undefined} payload
 * @param {HTMLFormElement|null} form
 * @returns {string}
 */
function emailFromPayload(payload) {
  const fromPayload = typeof payload?.email === 'string' ? payload.email : '';

  return fromPayload.includes('@') ? fromPayload : '';
}

/**
 * @param {Record<string, unknown>} payload
 */
function syncConfirmedSubscriberMark(payload) {
  if (!subscriberIsConfirmed(payload)) {
    forgetConfirmedSubscriberMark();
    return;
  }

  rememberConfirmedSubscriber(emailFromPayload(payload));
}

function forgetConfirmedSubscriberMark() {
  if (readSubscriberCookie().initials === '') {
    return;
  }

  clearConfirmedSubscriber();
}

/**
 * @param {Record<string, unknown>} payload
 */
function subscriberIsConfirmed(payload) {
  const normalized = normalizePreferencePayload(payload);
  const status = String(normalized.status ?? '').toLowerCase();

  if (status === 'active') {
    return true;
  }

  const code = new URLSearchParams(globalThis.location?.search ?? '').get('status') ?? '';

  return code === 'confirmed';
}

function rememberConfirmedSubscriber(email) {
  if (!email.includes('@')) {
    return;
  }

  writeSubscriberCookie(initialsFromEmail(email), currentPreferencesUrl());
  applyHeaderSubscriberMark();
}

function writeSubscriberCookie(initials, preferencesUrl) {
  writeNamedCookie(
    SUBSCRIBER_INITIALS_COOKIE,
    preferencesUrl === '' ? initials : `${initials}|${preferencesUrl}`,
  );
}

function updateSubscriberCookiePreferencesUrl(preferencesUrl) {
  const { initials } = readSubscriberCookie();

  if (initials === '' || preferencesUrl === '') {
    return;
  }

  writeSubscriberCookie(initials, preferencesUrl);
}

function clearConfirmedSubscriber() {
  writeNamedCookie(SUBSCRIBER_INITIALS_COOKIE, '');
  applyHeaderSubscriberMark();
}

function currentPreferencesUrl() {
  const token = readToken();

  if (token === '') {
    return '';
  }

  const path = globalThis.location?.pathname && globalThis.location.pathname !== ''
    ? globalThis.location.pathname
    : '/preferences/';

  return `${path}?token=${encodeURIComponent(token)}`;
}

function writeNamedCookie(name, value) {
  const secure = globalThis.location?.protocol === 'https:' ? '; Secure' : '';
  const maxAge = value === '' ? 0 : 60 * 60 * 24 * 90;
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
}

function readSubscriberCookie() {
  const raw = readNamedCookie(SUBSCRIBER_INITIALS_COOKIE);
  const split = raw.indexOf('|');

  if (split === -1) {
    return { initials: raw, url: '' };
  }

  return { initials: raw.slice(0, split), url: raw.slice(split + 1) };
}

function readNamedCookie(name) {
  const parts = String(document.cookie ?? '').split(';');
  let value = '';

  for (const part of parts) {
    const [cookieName, ...rest] = part.trim().split('=');

    if (cookieName === name) {
      value = decodeURIComponent(rest.join('='));
    }
  }

  return value;
}

function applyHeaderSubscriberMark() {
  applySubscriberEntryPoints();
}

function applySubscriberEntryPoints() {
  const preferencesUrl = readSubscriberCookie().url;
  const manageLinks = document.querySelectorAll?.('a.dt-footer-manage-preferences') ?? [];

  for (const manageLink of manageLinks) {
    if (!isElement(manageLink)) {
      continue;
    }

    manageLink.setAttribute('href', preferencesUrl || '#subscribe');

    if (preferencesUrl !== '') {
      bindPreferenceRefresh(manageLink);
    }
  }
}

function bindPreferenceRefresh(link) {
  if (link.dataset.dtRefreshBound === '1') {
    return;
  }

  link.dataset.dtRefreshBound = '1';
  link.addEventListener('click', (event) => {
    const href = link.getAttribute('href') || '';

    if (!href.includes('token=')) {
      return;
    }

    event.preventDefault();
    void openOrRefreshPreferences(link);
  });
}

async function openOrRefreshPreferences(link) {
  if (link.dataset.dtPreferenceRefresh === '1') {
    return;
  }

  link.dataset.dtPreferenceRefresh = '1';

  const href = link.getAttribute('href') || '';
  const token = new URL(href, globalThis.location?.origin ?? 'https://example.test').searchParams.get('token') ?? '';
  const refreshUrl = globalThis.dtPreferencesConfig?.refreshUrl
    || new URL('/wp-json/democratic-tools/v1/preferences/refresh', globalThis.location?.origin ?? 'https://example.test').toString();

  try {
    if (token === '') {
      globalThis.location?.assign?.('/preferences/');
      return;
    }

    const response = await fetch(refreshUrl, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    const payload = await response.json();

    if (typeof payload.redirect === 'string' && payload.redirect !== '') {
      updateSubscriberCookiePreferencesUrl(payload.redirect);
      globalThis.location.assign(payload.redirect);
      return;
    }

    showHeaderNote(link, 'We could not open your preferences. Try again.');
  } catch {
    showHeaderNote(link, 'We could not open your preferences. Try again.');
  } finally {
    delete link.dataset.dtPreferenceRefresh;
  }
}

function showHeaderNote(link, message) {
  let note = document.querySelector?.('.dt-header-cta-note');

  if (!isElement(note)) {
    note = document.createElement('p');
    note.className = 'dt-header-cta-note';
    (link.parentElement ?? document.body).appendChild(note);
  }

  note.textContent = message;
}

function setStatus(status, message, type) {
  if (!isElement(status)) {
    return;
  }

  const text = readableMessage(message);
  const form = typeof status.closest === 'function' ? status.closest('form') : null;
  const nodes = form ? [...form.querySelectorAll('.dt-form__status')] : [status];

  for (const node of nodes) {
    if (!isElement(node)) {
      continue;
    }

    node.textContent = text;
    node.hidden = text === '';
    node.classList.toggle('dt-form__status--success', type === 'success');
    node.classList.toggle('dt-form__status--error', type === 'error');
  }

  if (text !== '') {
    scrollStatusIntoView(status);
  }
}

function scrollStatusIntoView(status) {
  const rect = typeof status.getBoundingClientRect === 'function' ? status.getBoundingClientRect() : null;
  const current = globalThis.scrollY ?? globalThis.pageYOffset;

  if (rect && typeof current === 'number' && typeof globalThis.scrollTo === 'function') {
    globalThis.scrollTo({
      top: Math.max(0, current + rect.top - 140),
      behavior: 'smooth',
    });
  }

  if (typeof status.scrollIntoView === 'function') {
    status.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

/**
 * Strip tags / decode entities so WP fatal HTML is shown as plain text.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function readableMessage(value) {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmed = value.trim();

  if (trimmed === '') {
    return '';
  }

  if (!trimmed.includes('<') && !trimmed.includes('&')) {
    return trimmed;
  }

  return trimmed
    .replace(/<\/(p|div|li|h[1-6])\s*>/gi, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * @param {Response} response
 * @returns {Promise<Record<string, unknown>>}
 */
async function parseJsonSafe(response) {
  try {
    const payload = await response.json();
    return payload && typeof payload === 'object' ? payload : {};
  } catch {
    return {};
  }
}

/**
 * @param {unknown} value
 * @returns {value is HTMLFormElement}
 */
function isFormElement(value) {
  return isElement(value) && value.tagName === 'FORM';
}

/**
 * @param {unknown} value
 * @returns {value is HTMLElement}
 */
function isElement(value) {
  return typeof value === 'object' && value !== null && 'tagName' in value && 'classList' in value;
}

/**
 * @param {unknown} value
 */
function isFieldControl(value) {
  if (!isElement(value)) {
    return false;
  }

  const tag = value.tagName;
  return tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA';
}
